import Widget from '../../../../components/Widget';
import { Form, Checkbox, TabsProps, Select } from 'antd';
import { useIntl } from 'react-intl';
import InputWrap from '../../../../components/InputWrap';
import TableWrap from '../../../../components/TableWrap';
import ButtonWrap from '../../../../components/ButtonWrap';
import './style.scss';
import SelectWrap from '../../../../components/SelectWrap';
import { useMonthAndDateSelect } from '../../../../hooks/useMonthAndDateSelect';
import { useMutation, useQuery } from '@tanstack/react-query';
import { HOLIDAY_API, RECRUITMENT_API } from '../../../../constants/QueryKey';
import { recruitmentTypeApi, regulationConfigApi } from '../../../../apis';
import { useEffect, useState } from 'react';
import {
  DataTimeGrantDto,
  UpdateLeaveRegulationConfigDto,
  UpdateLeaveRegulationConfigDtoBaseOnEnum,
} from '../../../../apis/client-axios';
import ToastSuccess from '../../../../components/Toast/ToastSuccess';
import ToastError from '../../../../components/Toast/ToastError';

const dayPerWeek = [
  {
    value: 1,
    label: '1',
  },
  {
    value: 2,
    label: '2',
  },
  {
    value: 3,
    label: '3',
  },
  {
    value: 4,
    label: '4',
  },
  {
    value: 5,
    label: '5',
  },
];

const Holiday = () => {
  const [form] = Form.useForm();
  const intl = useIntl();

  const { day, setDay, month, setMonth, dayOptions, monthOptions } = useMonthAndDateSelect();

  const [selectedDayPerWeek, setSelectedDayPerWeek] = useState<UpdateLeaveRegulationConfigDtoBaseOnEnum>();
  const [isSetting2ndBase, setIsSetting2ndBase] = useState<boolean>(false);
  const [activeTabKey, setActiveTabKey] = useState<string>();

  const { data: regulaData, isLoading: isLoadingLeaveRegulation } = useQuery({
    queryKey: [HOLIDAY_API, activeTabKey],
    queryFn: () => regulationConfigApi.leaveRegulationConfigControllerGetAllByRecruitmentTypeId(activeTabKey as string),
    enabled: !!activeTabKey,
  });

  const { data: tabApiData, isLoading: isLoadingRecruitment } = useQuery({
    queryKey: [RECRUITMENT_API],
    queryFn: () => recruitmentTypeApi.recruitmentTypeControllerGetAll(1),
  });

  useEffect(() => {
    if (Array.isArray(tabApiData?.data?.data?.content) && tabApiData?.data?.data?.content?.length && !activeTabKey) {
      setActiveTabKey(tabApiData?.data?.data?.content?.[0]?.id);
    }
  }, [tabApiData?.data?.data?.content, activeTabKey]);

  const updateRegulationMutation = useMutation({
    mutationFn: ({ payload, id }: { payload: UpdateLeaveRegulationConfigDto; id: string }) =>
      regulationConfigApi.leaveRegulationConfigControllerUpdateByRecruitmentTypeId(id, payload),
    onSuccess: () => {
      ToastSuccess({
        content: intl.formatMessage({ id: 'holiday.noti.success' }),
      });
    },
    onError: () => {
      ToastError({
        content: intl.formatMessage({ id: 'holiday.noti.fail' }),
      });
    },
  });

  const handleUpdateRegulation = (data) => {
    const dataTimeGrant: DataTimeGrantDto[] = [];
    Object.keys(data).forEach((key) => {
      if (key.includes('row-')) {
        const [, attributeName, dayPerWeek] = key.split('-');
        const indexExistDayPerWeek = dataTimeGrant.findIndex((tg) => tg.dayPerWeek === Number(dayPerWeek));
        if (indexExistDayPerWeek >= 0) {
          dataTimeGrant[indexExistDayPerWeek] = {
            ...dataTimeGrant[indexExistDayPerWeek],
            [attributeName]: data[key],
          };
        } else {
          dataTimeGrant.push({
            dayPerWeek: Number(dayPerWeek),
            dayPerYear: 0,
            half1stYear: 0,
            half2ndYear: 0,
            half3rdYear: 0,
            half4thYear: 0,
            half5thYear: 0,
            half6thYear: 0,
            aboveHalf7thYear: 0,
            [attributeName]: data[key],
          });
        }
      }
    });
    const payload = {
      baseOn: selectedDayPerWeek as UpdateLeaveRegulationConfigDtoBaseOnEnum,
      dayPerWeek: form.getFieldValue('dayPerWeek'),
      firstGrantAfter: form.getFieldValue('firstGrantAfter'),
      isSetting2ndBase,
      dayOf2ndBase: form.getFieldValue('dayOf2ndBase'),
      monthOf2ndBase: form.getFieldValue('monthOf2ndBase'),
      isDividedHalfYear: form.getFieldValue('isDividedHalfYear'),
      isPriority2ndBase: form.getFieldValue('isPriority2ndBase'),
      dataTimeGrant,
    };
    updateRegulationMutation.mutate({ id: activeTabKey as string, payload });
  };

  const onClickTabChange = (key: string) => {
    form.resetFields();
    setActiveTabKey(key);
  };

  const columns = [
    {
      title: (
        <div className="first-header-cell">
          <span>{intl.formatMessage({ id: 'holiday.table.weeklyContract' })}</span>
          <span>{intl.formatMessage({ id: 'holiday.table.number.working' })}</span>
        </div>
      ),
      dataIndex: 'dayPerWeek',
      key: 'dayPerWeek',
      render: (record, _, index) => {
        return (
          <div className="holiday_content-bottom-table-eightyPercentAttendance">
            <p className="application-approval_content-data">{record}</p>
            {intl.formatMessage({ id: 'holiday.day' })}
            {index === 0 && intl.formatMessage({ id: 'holiday.more' })}
          </div>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'holiday.table.schedule' }),
      dataIndex: 'dayPerYear',
      key: 'dayPerYear',
      className: 'second-table-cell',
      render: (value, record, row) => {
        return (
          <div className="holiday_content-bottom-table-annualWorkDays">
            <Form.Item
              rules={[
                {
                  message: '',
                  required: true,
                },
                {
                  message: '',
                  whitespace: true,
                },
                {
                  validator: validatePositiveInteger,
                },
              ]}
              name={`row-dayPerYear-${record.dayPerWeek}`}
              className="mb-0"
            >
              <InputWrap maxLength={255} className="holiday_content-bottom-table-input-row1" />
            </Form.Item>
            <span>{intl.formatMessage({ id: 'holiday.day' })}</span>
          </div>
        );
      },
    },
    {
      className: 'border_line-cell',
      title: intl.formatMessage({ id: 'holiday.table.grantDate' }),
      children: [
        {
          title: intl.formatMessage({ id: 'holiday.table.halfYear' }),
          className: 'border_line-cell',
          children: [
            {
              title: intl.formatMessage({ id: 'holiday.table.perfectAttend' }),
              dataIndex: 'half1stYear',
              className: 'border_line-cell',
              key: 'half1stYear',
              render: (value, record, row) => {
                return (
                  <div className="holiday_content-bottom-table-eightyPercentAttendance">
                    <Form.Item
                      name={`row-half1stYear-${record.dayPerWeek}`}
                      className="mb-0"
                      rules={[
                        {
                          required: true,
                          message: '',
                        },
                        {
                          message: '',
                          whitespace: true,
                        },
                        {
                          validator: validatePositiveInteger,
                        },
                      ]}
                    >
                      <InputWrap maxLength={255} className="holiday_content-bottom-table-input-row2" />
                    </Form.Item>
                    <span>{intl.formatMessage({ id: 'holiday.day' })}</span>
                  </div>
                );
              },
            },
          ],
        },
        {
          title: intl.formatMessage({ id: 'holiday.year' }, { year: 1.5 }),
          className: 'border_line-cell',
          children: [
            {
              title: intl.formatMessage({ id: 'holiday.table.perfectAttend' }),
              className: 'border_line-cell',
              dataIndex: 'half2ndYear',
              key: 'half2ndYear',
              render: (value, record, row) => {
                return (
                  <div className="holiday_content-bottom-table-eightyPercentAttendance">
                    <Form.Item
                      name={`row-half2ndYear-${record.dayPerWeek}`}
                      className="mb-0"
                      rules={[
                        {
                          message: '',
                          required: true,
                        },
                        {
                          message: '',
                          whitespace: true,
                        },
                        {
                          validator: validatePositiveInteger,
                        },
                      ]}
                    >
                      <InputWrap maxLength={255} className="holiday_content-bottom-table-input-row2" />
                    </Form.Item>
                    <span>{intl.formatMessage({ id: 'holiday.day' })}</span>
                  </div>
                );
              },
            },
          ],
        },
        {
          title: intl.formatMessage({ id: 'holiday.year' }, { year: 2.5 }),
          className: 'border_line-cell',
          children: [
            {
              title: intl.formatMessage({ id: 'holiday.table.perfectAttend' }),
              dataIndex: 'half3rdYear',
              className: 'border_line-cell',
              key: 'half3rdYear',
              render: (value, record, row) => {
                return (
                  <div className="holiday_content-bottom-table-eightyPercentAttendance">
                    <Form.Item
                      name={`row-half3rdYear-${record.dayPerWeek}`}
                      className="mb-0"
                      rules={[
                        {
                          message: '',
                          required: true,
                        },
                        {
                          message: '',
                          whitespace: true,
                        },
                        {
                          validator: validatePositiveInteger,
                        },
                      ]}
                    >
                      <InputWrap maxLength={255} className="holiday_content-bottom-table-input-row2" />
                    </Form.Item>
                    <span>{intl.formatMessage({ id: 'holiday.day' })}</span>
                  </div>
                );
              },
            },
          ],
        },
        {
          title: intl.formatMessage({ id: 'holiday.year' }, { year: 3.5 }),
          className: 'border_line-cell',
          children: [
            {
              title: intl.formatMessage({ id: 'holiday.table.perfectAttend' }),
              className: 'border_line-cell',
              dataIndex: 'half4thYear',
              key: 'half4thYear',
              render: (value, record, row) => {
                return (
                  <div className="holiday_content-bottom-table-eightyPercentAttendance">
                    <Form.Item
                      name={`row-half4thYear-${record.dayPerWeek}`}
                      className="mb-0"
                      rules={[
                        {
                          message: '',
                          required: true,
                        },
                        {
                          message: '',
                          whitespace: true,
                        },
                        {
                          validator: validatePositiveInteger,
                        },
                      ]}
                    >
                      <InputWrap maxLength={255} className="holiday_content-bottom-table-input-row2" />
                    </Form.Item>
                    <span>{intl.formatMessage({ id: 'holiday.day' })}</span>
                  </div>
                );
              },
            },
          ],
        },
        {
          className: 'border_line-cell',
          title: intl.formatMessage({ id: 'holiday.year' }, { year: 4.5 }),
          children: [
            {
              title: intl.formatMessage({ id: 'holiday.table.perfectAttend' }),
              className: 'border_line-cell',
              dataIndex: 'half5thYear',
              key: 'half5thYear',
              render: (value, record, row) => {
                return (
                  <div className="holiday_content-bottom-table-eightyPercentAttendance">
                    <Form.Item
                      name={`row-half5thYear-${record.dayPerWeek}`}
                      className="mb-0"
                      rules={[
                        {
                          message: '',
                          required: true,
                        },
                        {
                          message: '',
                          whitespace: true,
                        },
                        {
                          validator: validatePositiveInteger,
                        },
                      ]}
                    >
                      <InputWrap maxLength={255} className="holiday_content-bottom-table-input-row2" />
                    </Form.Item>
                    <span>{intl.formatMessage({ id: 'holiday.day' })}</span>
                  </div>
                );
              },
            },
          ],
        },
        {
          className: 'border_line-cell',
          title: intl.formatMessage({ id: 'holiday.year' }, { year: 5.5 }),
          children: [
            {
              title: intl.formatMessage({ id: 'holiday.table.perfectAttend' }),
              className: 'border_line-cell',
              dataIndex: 'half6thYear',
              key: 'half6thYear',
              render: (value, record, row) => {
                return (
                  <div className="holiday_content-bottom-table-eightyPercentAttendance">
                    <Form.Item
                      name={`row-half6thYear-${record.dayPerWeek}`}
                      className="mb-0"
                      rules={[
                        {
                          message: '',
                          required: true,
                        },
                        {
                          message: '',
                          whitespace: true,
                        },
                        {
                          validator: validatePositiveInteger,
                        },
                      ]}
                    >
                      <InputWrap maxLength={255} className="holiday_content-bottom-table-input-row2" />
                    </Form.Item>
                    <span>{intl.formatMessage({ id: 'holiday.day' })}</span>
                  </div>
                );
              },
            },
          ],
        },
        {
          className: 'border_line-cell',
          title: intl.formatMessage({ id: 'holiday.yearMoreThan' }, { year: 6.5 }),
          children: [
            {
              title: intl.formatMessage({ id: 'holiday.table.perfectAttend' }),
              className: 'border_line-cell',
              dataIndex: 'aboveHalf7thYear',
              key: 'aboveHalf7thYear',
              render: (value, record, row) => {
                return (
                  <div className="holiday_content-bottom-table-eightyPercentAttendance">
                    <Form.Item
                      name={`row-aboveHalf7thYear-${record.dayPerWeek}`}
                      className="mb-0"
                      rules={[
                        {
                          message: '',
                          required: true,
                        },
                        {
                          message: '',
                          whitespace: true,
                        },
                        {
                          validator: validatePositiveInteger,
                        },
                      ]}
                    >
                      <InputWrap maxLength={255} className="holiday_content-bottom-table-input-row2" />
                    </Form.Item>
                    <span>{intl.formatMessage({ id: 'holiday.day' })}</span>
                  </div>
                );
              },
            },
          ],
        },
      ],
    },
  ];

  const items: TabsProps['items'] = Array.isArray(tabApiData?.data?.data?.content)
    ? tabApiData?.data?.data?.content?.map((item) => ({
        key: item.id,
        label: item.type,
      }))
    : [];

  useEffect(() => {
    if (regulaData?.data.data) {
      const {
        dataTimeGrant,
        baseOn,
        isSetting2ndBase,
        dayPerWeek,
        firstGrantAfter,
        isPriority2ndBase,
        isDividedHalfYear,
        monthOf2ndBase,
        dayOf2ndBase,
      } = regulaData?.data?.data;

      setSelectedDayPerWeek(
        baseOn === UpdateLeaveRegulationConfigDtoBaseOnEnum.Week
          ? UpdateLeaveRegulationConfigDtoBaseOnEnum.Week
          : UpdateLeaveRegulationConfigDtoBaseOnEnum.Year,
      );

      setIsSetting2ndBase(!!isSetting2ndBase);

      const formValue = {};

      dataTimeGrant?.forEach((item) => {
        const {
          dayPerWeek,
          dayPerYear,
          half1stYear,
          half2ndYear,
          half3rdYear,
          half4thYear,
          half5thYear,
          half6thYear,
          aboveHalf7thYear,
        } = item;

        formValue[`row-dayPerYear-${dayPerWeek}`] = dayPerYear;
        formValue[`row-half1stYear-${dayPerWeek}`] = half1stYear;
        formValue[`row-half2ndYear-${dayPerWeek}`] = half2ndYear;
        formValue[`row-half3rdYear-${dayPerWeek}`] = half3rdYear;
        formValue[`row-half4thYear-${dayPerWeek}`] = half4thYear;
        formValue[`row-half5thYear-${dayPerWeek}`] = half5thYear;
        formValue[`row-half6thYear-${dayPerWeek}`] = half6thYear;
        formValue[`row-aboveHalf7thYear-${dayPerWeek}`] = aboveHalf7thYear;
      });

      form.setFieldsValue({
        dayPerWeek,
        firstGrantAfter,
        isPriority2ndBase,
        isDividedHalfYear,
        monthOf2ndBase,
        dayOf2ndBase,
        ...formValue,
      });
    }
  }, [regulaData?.data.data]);

  const validatePositiveInteger = (_, value) => {
    if (!value || !/^[1-9]\d*$/.test(value)) {
      return Promise.reject();
    }
    return Promise.resolve();
  };

  return (
    <div className="holiday">
      <Widget
        breadcrumbs={[intl.formatMessage({ id: 'holiday.breadCrumb' })]}
        tabProps={{
          items,
          onChange: (key: string) => {
            onClickTabChange(key);
          },
        }}
        content={
          <div className="holiday_content">
            <Form form={form} layout="vertical" onFinish={handleUpdateRegulation}>
              <div className="holiday_content-top">
                <div className="holiday_content-top-firstOption">
                  <p className="holiday_content-title">{intl.formatMessage({ id: 'holiday.title.dayPerWeek' })}</p>
                  <div className="holiday_content-top-radio">
                    <div className="holiday_content-top-radio-option">
                      <input
                        checked={selectedDayPerWeek === UpdateLeaveRegulationConfigDtoBaseOnEnum.Week}
                        onChange={() => setSelectedDayPerWeek(UpdateLeaveRegulationConfigDtoBaseOnEnum.Week)}
                        type="radio"
                        name="dayPerWeek"
                      />
                      <Form.Item
                        name="dayPerWeek"
                        rules={[
                          {
                            required: selectedDayPerWeek === UpdateLeaveRegulationConfigDtoBaseOnEnum.Week,
                            message: '',
                          },
                        ]}
                      >
                        <Select
                          disabled={selectedDayPerWeek !== UpdateLeaveRegulationConfigDtoBaseOnEnum.Week}
                          options={dayPerWeek}
                          className="holiday_options-select-day"
                        />
                      </Form.Item>
                      <span>{intl.formatMessage({ id: 'holiday.day' })}</span>
                    </div>
                    <div className="holiday_content-top-radio-option">
                      <input
                        checked={selectedDayPerWeek === UpdateLeaveRegulationConfigDtoBaseOnEnum.Year}
                        onChange={() => setSelectedDayPerWeek(UpdateLeaveRegulationConfigDtoBaseOnEnum.Year)}
                        type="radio"
                        name="dayPerWeek"
                      />
                      <span>{intl.formatMessage({ id: 'holiday.option.caculate' })}</span>
                    </div>
                  </div>
                </div>

                <div className="holiday_content-top-input">
                  <p className="holiday_content-title">{intl.formatMessage({ id: 'holiday.title.baseDate' })}</p>
                  <div className="holiday_content-top-input-option">
                    <span>{intl.formatMessage({ id: 'holiday.dayJoining' })}</span>
                    <Form.Item name="firstGrantAfter">
                      <InputWrap disabled className="holiday_content-top-input" />
                    </Form.Item>
                    <span>{intl.formatMessage({ id: 'holiday.firstGrant' })}</span>
                  </div>
                </div>

                <div className="holiday_content-top-firstOption">
                  <p className="holiday_content-title">
                    {intl.formatMessage({ id: 'holiday.title.seccondReference' })}
                  </p>

                  <div className="holiday_content-top-radio-option">
                    <input
                      checked={isSetting2ndBase === false}
                      onChange={() => setIsSetting2ndBase(false)}
                      type="radio"
                      name="timeOf2ndBase"
                    />
                    <span>{intl.formatMessage({ id: 'holiday.none' })}</span>
                  </div>

                  <div className="holiday_content-top-radio">
                    <div className="holiday_content-top-radio-option">
                      <input
                        checked={isSetting2ndBase === true}
                        onChange={() => {
                          setIsSetting2ndBase(true);
                          form.setFieldValue('monthOf2ndBase', 1);
                          form.setFieldValue('dayOf2ndBase', 1);
                        }}
                        type="radio"
                        name="timeOf2ndBase"
                      />
                      <span className="m-r-8">{intl.formatMessage({ id: 'holiday.grantDate' })}</span>
                      <Form.Item
                        rules={[
                          {
                            required: isSetting2ndBase,
                            message: '',
                          },
                        ]}
                        name="monthOf2ndBase"
                      >
                        <SelectWrap
                          className="holiday_options-select-month"
                          value={month}
                          onChange={(value: number) => {
                            setMonth(value);
                          }}
                          disabled={!isSetting2ndBase}
                          options={monthOptions.map((item: number) => {
                            return { value: item, label: item };
                          })}
                        />
                      </Form.Item>
                      <span className="m-r-8">{intl.formatMessage({ id: 'holiday.month' })}</span>
                      <Form.Item
                        rules={[
                          {
                            required: isSetting2ndBase,
                            message: '',
                          },
                        ]}
                        name="dayOf2ndBase"
                      >
                        <SelectWrap
                          className="holiday_options-select-month"
                          value={day}
                          onChange={(value: number) => {
                            setDay(value);
                          }}
                          disabled={!isSetting2ndBase}
                          options={dayOptions.map((item: number) => {
                            return { value: item, label: item };
                          })}
                        />
                      </Form.Item>
                      <span>{intl.formatMessage({ id: 'holiday.unifiDay' })}</span>
                    </div>
                  </div>
                </div>

                <div className="holiday_options-input-checkbox">
                  <Form.Item name="isDividedHalfYear" className="holiday_content-top-inputdate" valuePropName="checked">
                    <Checkbox defaultChecked={regulaData?.data.data?.isDividedHalfYear}>
                      <span>{intl.formatMessage({ id: 'holiday.checkbox.determind' })}</span>
                    </Checkbox>
                  </Form.Item>
                  <Form.Item name="isPriority2ndBase" className="holiday_content-top-inputdate" valuePropName="checked">
                    <Checkbox defaultChecked={regulaData?.data.data?.isPriority2ndBase}>
                      <span>{intl.formatMessage({ id: 'holiday.checkbox.prioritize' })}</span>
                    </Checkbox>
                  </Form.Item>
                </div>
              </div>
              <div className="holiday_content-bottom">
                <TableWrap
                  isPagination={false}
                  loading={isLoadingLeaveRegulation || isLoadingRecruitment}
                  tableProps={{
                    dataSource: regulaData?.data.data?.dataTimeGrant || [],
                    columns,
                    className: 'holiday_content-bottom-table',
                  }}
                />
                <div className="holiday_content-bottom-button">
                  <ButtonWrap
                    htmlType="submit"
                    className="holiday_content-bottom-save"
                    disabled={updateRegulationMutation.isPending}
                  >
                    {intl.formatMessage({ id: 'holiday.button.save' })}
                  </ButtonWrap>
                </div>
              </div>
            </Form>
          </div>
        }
        title={intl.formatMessage({ id: 'holiday.title' })}
      />
    </div>
  );
};
export default Holiday;
